/**
 * ============================================================================
 *  CONTENT CONFIG — Metalotsen
 * ============================================================================
 *  Alle sichtbaren Texte, Bilder, Referenzen und Testimonials liegen in
 *  dieser einen Datei. Änderungen können hier ohne Programmierkenntnisse
 *  vorgenommen werden. Nach einer Änderung: Seite neu laden – fertig.
 *
 *  Wichtig:
 *  - Bilder sollten öffentlich erreichbare URLs sein (z. B. Unsplash oder
 *    ein eigener CDN-/Hoster-Link). Empfohlene Breite: 1200–1600 px.
 *  - Für neue Referenzen einfach ein weiteres Objekt an das `references`
 *    Array anhängen. Reihenfolge = Sortierung auf der Seite.
 *  - Für neue Stimmen: neuen Eintrag in `testimonials` ergänzen.
 * ============================================================================
 */

export const site = {
  name: "Metalotsen",
  owner: "Björn Richter",
  address: {
    street: "Ludwig-Hupfeld-Straße 1a",
    zip: "04179",
    city: "Leipzig",
    country: "Deutschland",
  },
  phone: "0155 - 674 271 94",
  email: "hallo@metalotsen.de",
  maps: {
    // Öffentlicher Google-Maps-Embed (kein API-Key nötig).
    embed:
      "https://www.google.com/maps?q=Ludwig-Hupfeld-Stra%C3%9Fe+1a,+04179+Leipzig&output=embed",
    link: "https://maps.google.com/?q=Ludwig-Hupfeld-Stra%C3%9Fe+1a,+04179+Leipzig",
  },
  // Optional: später mit Calendly / Microsoft Bookings URL füllen.
  // Wird der Wert gesetzt, blendet Contact.jsx automatisch einen
  // Terminbuchungs-Slot neben dem Formular ein.
  scheduling: {
    provider: null, // "calendly" | "bookings" | null
    embedUrl: "",   // z. B. "https://calendly.com/metalotsen/erstgespraech"
    label: "Direkt einen Termin buchen",
  },
  // Portrait-Bild für die "Über"-Sektion. Original-Foto von Björn Richter.
  portrait: {
    url: "/uploads/bjoern-richter.jpg",
    alt: "Björn Richter, Gründer von Metalotsen — Digitalagentur Leipzig",
  },
};

export const nav = [
  { id: "start", label: "Start", href: "#start" },
  { id: "leistungen", label: "Leistungen", href: "#leistungen" },
  { id: "referenzen", label: "Referenzen", href: "#referenzen" },
  { id: "ueber", label: "Über", href: "#ueber" },
  { id: "kontakt", label: "Kontakt", href: "#kontakt" },
];

export const services = [
  {
    id: "webdesign",
    kicker: "01",
    title: "Webdesign",
    lead: "Webseiten, die verkaufen — nicht nur gefallen.",
    body:
      "Von der Landingpage bis zur Corporate Website. Technisch sauber, SEO-optimiert und responsive auf jedem Gerät.",
    tags: ["SEO", "Responsive", "Landingpages", "Corporate Websites"],
    icon: "Layout",
  },
  {
    id: "social",
    kicker: "02",
    title: "Social Media",
    lead: "Reichweite mit Substanz — statt beliebiger Content-Flut.",
    body:
      "Strategisch geplant, redaktionell durchdacht, plattformnativ umgesetzt. Für Marken, die gehört werden wollen.",
    tags: ["Facebook", "Instagram", "LinkedIn", "Pinterest", "TikTok", "Bluesky"],
    icon: "Sparkles",
  },
  {
    id: "ki",
    kicker: "03",
    title: "Künstliche Intelligenz",
    lead: "KI als Werkzeug — nicht als Selbstzweck.",
    body:
      "Wir kombinieren ChatGPT, Gemini und generative Bild- & Videomodelle mit echten Prozessen. Messbar, ethisch, wirksam.",
    tags: ["ChatGPT", "Gemini", "KI-Bilder", "KI-Videos", "Automation"],
    icon: "Cpu",
  },
  {
    id: "recruiting",
    kicker: "04",
    title: "Recruiting",
    lead: "Menschen finden, die passen — nicht nur bewerben.",
    body:
      "Employer Branding, Social Recruiting und Job Marketing. Inklusive Anbindung an unsere Plattform ClaRic.",
    tags: ["Employer Branding", "Social Recruiting", "Job Marketing", "ClaRic"],
    icon: "Users",
  },
];

export const reasons = [
  { title: "Persönliche Betreuung", body: "Ein direkter Ansprechpartner. Kein Ticket-System, keine Callcenter-Kette.", icon: "MessageCircle" },
  { title: "Moderne Technologie", body: "Aktuelle Frameworks, Performance-first Architektur, saubere Standards.", icon: "Layers" },
  { title: "Kreative Ideen", body: "Konzepte, die aus Ihrer Marke entstehen — nicht aus einer Vorlage.", icon: "Lightbulb" },
  { title: "Messbare Ergebnisse", body: "KPIs, die zu Ihrem Geschäft passen. Reporting, das man wirklich liest.", icon: "LineChart" },
  { title: "Schnelle Kommunikation", body: "Antwort meist am selben Werktag. Klare Wege statt Umwege.", icon: "Zap" },
  { title: "Langfristige Partnerschaft", body: "Wir denken über Projekte hinaus. Kunden bleiben Jahre — nicht Wochen.", icon: "Handshake" },
];

/**
 * REFERENZEN
 * Jede Referenz besteht aus:
 *   - name         : Projekt- / Firmenname
 *   - subtitle     : Kurze Kategorie (erscheint oben links auf der Karte)
 *   - year         : Zeitraum / Jahr (oben rechts)
 *   - role         : Leistungen, getrennt durch " · "
 *   - body         : Kurzbeschreibung (2–3 Sätze reichen)
 *   - image        : Öffentlich erreichbare Bild-URL oder Pfad unter /uploads
 *   - logo         : optional – Kunden-Logo (transparent oder auf hellem Grund)
 *   - website      : optional – Website des Kunden (öffnet in neuem Tab)
 *   - status       : optional – „Live", „Ongoing", „Case Study" …
 *   - results      : optional – Array kurzer KPI-Sätze
 *   - technologies : optional – Array eingesetzter Tools/Plattformen
 */
export const references = [
  {
    id: "claric",
    name: "ClaRic",
    subtitle: "Deutschlandweite Jobplattform",
    year: "seit 2024",
    role: "Recruiting · SEO · Landingpages · Marketing",
    body:
      "Eine Recruiting-Plattform, die Bewerbungen radikal vereinfacht. Wir verantworten Positionierung, Landingpages, SEO und den kontinuierlichen Ausbau der Sichtbarkeit.",
    image: "/uploads/claric-logo.png",
    imageMode: "logo", // "logo" = zentriert, farbiger Hintergrund | "cover" = klassisch
    logo: "/uploads/claric-logo.png",
    website: "https://claric.de",
    status: "Live · Ongoing",
    results: ["+312 % qual. Bewerbungen", "Top-3 Rankings", "45 % kürzere Time-to-Hire"],
    technologies: ["WordPress", "SEO", "Landingpages", "Marketing Automation"],
  },
  {
    id: "kleinig",
    name: "KLEINIG engineering",
    subtitle: "Industrie · Automatisierung · Maschinenbau",
    year: "seit 2024",
    role: "LinkedIn · Industrial Marketing · Content · Video",
    body:
      "Sichtbarkeit in einer Branche, die Sichtbarkeit nicht kennt. Wir übersetzen Ingenieurwissen in klare, LinkedIn-taugliche Formate — ohne den Ingenieur zu verlieren.",
    image: "/uploads/kleinig-logo.png",
    imageMode: "logo",
    logo: "/uploads/kleinig-logo.png",
    website: "https://www.kleinig.gmbh",
    status: "Ongoing",
    results: ["+180 % LinkedIn-Reichweite", "Fachpublikum erreicht", "Neue B2B-Leads"],
    technologies: ["LinkedIn", "Video", "Content-Strategie", "Employer Branding"],
  },
  {
    id: "fox",
    name: "fox-COURIER",
    subtitle: "Markenentwicklung · Storytelling · Social Media",
    year: "seit 2023",
    role: "Branding · Storytelling · Social Media · Corporate Design",
    body:
      "Vom Kurierdienst zur Marke. Ein Erscheinungsbild, das auf der Straße funktioniert — und auf dem Bildschirm. Inklusive Maskottchen, Content-Universum und einheitlichem Auftritt.",
    image: "/uploads/fox-courier.jpg",
    imageMode: "cover",
    logo: null,
    website: "https://www.fox-courier.de",
    status: "Live · Case Study",
    results: ["Marken-Relaunch", "Konsistentes Erscheinungsbild", "Neuer Content-Kanal"],
    technologies: ["Branding", "Corporate Design", "Instagram", "Storytelling"],
  },
];

export const workflow = [
  { n: "01", title: "Erstgespräch", body: "Wir hören zu. Verstehen Kontext, Ziele, Zwänge. Ohne Verkaufsdruck.", duration: "Kostenlos · 30 Min." },
  { n: "02", title: "Strategie", body: "Positionierung, Kanäle, Metriken. Ein Plan, der zu Ihrem Geschäft passt.", duration: "1–2 Wochen" },
  { n: "03", title: "Umsetzung", body: "Design, Entwicklung, Content. Iterativ, mit wöchentlichen Fortschritten.", duration: "4–12 Wochen" },
  { n: "04", title: "Optimierung", body: "A/B-Tests, Reporting, Feinschliff. Daten führen Entscheidungen.", duration: "Fortlaufend" },
  { n: "05", title: "Wachstum", body: "Neue Kanäle, neue Zielgruppen, neue Erlösquellen. Skalieren mit Substanz.", duration: "Langfristig" },
];

export const testimonials = [
  {
    quote:
      "Metalotsen hat unsere Bewerberzahlen in wenigen Monaten verdreifacht. Selten so pragmatisch und gleichzeitig kreativ gearbeitet.",
    author: "Dr. Marlene Krüger",
    role: "Geschäftsführung, ClaRic",
  },
  {
    quote:
      "Björn versteht Technik und Kommunikation. Das ist selten und in unserer Branche unbezahlbar.",
    author: "Andreas Kleinig",
    role: "Inhaber, KLEINIG engineering",
  },
  {
    quote:
      "Endlich eine Agentur, die zuhört, bevor sie Antworten verkauft. Wir arbeiten seit zwei Jahren mit Metalotsen.",
    author: "Julia Fenner",
    role: "Marketing-Leitung, fox-COURIER",
  },
];

export const techLogos = [
  "Google",
  "Meta",
  "LinkedIn",
  "ChatGPT",
  "Gemini",
  "WordPress",
  "Canva",
  "Perplexity",
];
